import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import AreaZone from "./AreaZone";
import {Box, HStack, Input, Select, Stack, Text} from "@chakra-ui/react";
import ManagedCheckBox from "../../../components/form/ManagedCheckBox";
import {AppContext, AppContextI} from "../../../components/context/ContextWrapper";
import {MaintenanceZone as MaintenanceZoneType} from "../../../components/types";
import {ZoneContext, ZoneContextI} from "../../../components/context/ZoneContextWrapper";
import HelpTooltip from "../../../components/help/HelpTooltip";

type Props = {
    zone?: MaintenanceZoneType,
    active: boolean
}

const MaintenanceZone = ({zone, active}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const bleEnabled = appContext.isBleEnabled();
    const [resetApps, setResetApps] = useState<boolean>(zone?.resetApps ?? false);
    const [beaconSensitivity, setBeaconSensitivity] = useState<number>(zone?.beaconSensitivity ?? 8);
    const [coolDownPeriod, setCoolDownPeriod] = useState<number>(zone?.coolDownPeriod ?? 3);
    const [coolDownInput, setCoolDownInput] = useState<string>(String(zone?.coolDownPeriod ?? 3));

    const zoneContext: ZoneContextI = useContext<ZoneContextI>(ZoneContext);
    const zoneRef = useRef(zone);
    const updateZoneRef = useRef(appContext.updateZone);
    const setSelectedZoneRef = useRef(zoneContext.setSelectedZone);
    zoneRef.current = zone;
    updateZoneRef.current = appContext.updateZone;
    setSelectedZoneRef.current = zoneContext.setSelectedZone;

    useEffect(() => {
        if (active) {
            setSelectedZoneRef.current(zone?.id);
        }
    }, [active, zone?.id]);

    useEffect(() => {
        setResetApps(zone?.resetApps ?? false);
    }, [zone?.id, zone?.resetApps]);

    useEffect(() => {
        const currentZone = zoneRef.current;
        if (resetApps !== (currentZone?.resetApps ?? false)) {
            updateZoneRef.current(currentZone, {
                ...currentZone,
                resetApps: resetApps
            } as MaintenanceZoneType)
        }
    }, [resetApps]);

    useEffect(() => {
        setBeaconSensitivity(zone?.beaconSensitivity ?? 8);
    }, [zone?.id, zone?.beaconSensitivity]);

    useEffect(() => {
        const next = zone?.coolDownPeriod ?? 3;
        setCoolDownPeriod(next);
        setCoolDownInput(String(next));
    }, [zone?.id, zone?.coolDownPeriod]);

    useEffect(() => {
        const currentZone = zoneRef.current;
        const currentValue = currentZone?.coolDownPeriod ?? 3;
        if (coolDownPeriod !== currentValue) {
            updateZoneRef.current(currentZone, {
                ...currentZone,
                coolDownPeriod: coolDownPeriod
            } as MaintenanceZoneType);
        }
    }, [coolDownPeriod]);

    useEffect(() => {
        const currentZone = zoneRef.current;
        const currentSensitivity = currentZone?.beaconSensitivity ?? 8;
        if (beaconSensitivity !== currentSensitivity) {
            updateZoneRef.current(currentZone, {
                ...currentZone,
                beaconSensitivity: beaconSensitivity
            } as MaintenanceZoneType)
        }
    }, [beaconSensitivity]);

    if (zone) {
        const description = "The maintenance zone is where the phones can be updated, reset etc.";

        return <AreaZone description={description} zone={zone} soundFiles={[]}>
            <Stack>
                {bleEnabled ? (
                    <Box>
                        <HelpTooltip content={"Sets how strong a beacon signal must be before this zone triggers. Higher values are more sensitive and react from farther away; lower values keep the trigger tighter."}>
                            <Text fontWeight={"semibold"}>Beacon sensitivity:</Text>
                        </HelpTooltip>
                        <Select
                            name={"BeaconSensitivity"}
                            value={beaconSensitivity}
                            onChange={(event) => setBeaconSensitivity(+event.target.value)}
                            size={"sm"}
                            disabled={!appContext.isDraft()}
                        >
                            <option value={1}>1 (-50) - least sensitive</option>
                            <option value={2}>2 (-55)</option>
                            <option value={3}>3 (-60)</option>
                            <option value={4}>4 (-65)</option>
                            <option value={5}>5 (-70)</option>
                            <option value={6}>6 (-75)</option>
                            <option value={7}>7 (-80)</option>
                            <option value={8}>8 (-85) - default</option>
                            <option value={9}>9 (-90)</option>
                            <option value={10}>10 (-95) - most sensitive</option>
                        </Select>
                    </Box>
                ) : null}
                <HStack spacing={4} alignItems={"center"}>
                    <ManagedCheckBox name={"resetApps"} onChange={setResetApps} defaultValue={resetApps}
                                     disabled={!appContext.isDraft()}>
                        Reset apps
                    </ManagedCheckBox>
                    <HStack spacing={2} alignItems={"center"}>
                        <HelpTooltip content={"Minimum seconds after a maintenance reset before any spot or ambient can trigger again. 0–60, default 3."}>
                            <Text fontSize={"sm"} color={!resetApps ? "gray.400" : undefined}>
                                Cool-down (s):
                            </Text>
                        </HelpTooltip>
                        <Input
                            size={"sm"}
                            width={"70px"}
                            type={"number"}
                            min={0}
                            max={60}
                            step={1}
                            value={coolDownInput}
                            onChange={(event) => setCoolDownInput(event.target.value)}
                            onBlur={() => {
                                const parsed = parseInt(coolDownInput, 10);
                                const clamped = Number.isFinite(parsed) ? Math.max(0, Math.min(60, parsed)) : 3;
                                setCoolDownInput(String(clamped));
                                setCoolDownPeriod(clamped);
                            }}
                            isDisabled={!appContext.isDraft() || !resetApps}
                        />
                    </HStack>
                </HStack>
            </Stack>
        </AreaZone>
    } else {
        return <></>
    }
}
export default MaintenanceZone
